import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { stableStringify } from "../hashing/canonical-json.js";
/** Exact Slice A named outcomes (PlanningOS ENR-UX-008 §7F). */
export const ACCESS_INTAKE_READINESS_OUTCOMES = [
    "permission_denied",
    "expired_access",
    "revoked_access",
    "contradiction",
    "information_missing",
    "package_blocked",
    "ready_to_build",
    "intake_submitted",
    "intake_in_progress",
    "waiting_for_client",
    "client_access_ready",
    "new_project",
];
const PROHIBITED_CLIENT_TERMS = [
    "runId",
    "artifactSetHash",
    "exportBundleHash",
    "businessGraphRevisionId",
    "Quality Gate",
    "worker",
    "tenant",
    "schema",
    "GenerationRun",
    "INTAKE_OPEN",
    "INTAKE_IN_PROGRESS",
    "INTAKE_SUBMITTED",
    "EXPIRED",
    "REVOKED",
    "ELIGIBILITY_BLOCKED",
    "confirm-build",
    "review_and_build",
    "waiting_for_client",
];
const MISSING_BUSINESS_NAME_FALLBACK = "This client";
function resolveDisplayName(input) {
    const fromBusiness = input.businessName?.trim();
    if (fromBusiness)
        return fromBusiness;
    const fromClient = input.clientDisplayName?.trim();
    if (fromClient)
        return fromClient;
    return MISSING_BUSINESS_NAME_FALLBACK;
}
function projectIdOk(input) {
    return Boolean(input.projectId.trim());
}
function baseModel(partial) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        ...partial,
    });
}
function denialPair(outcome) {
    const copy = outcome === "permission_denied"
        ? {
            title: "Access is not available",
            reason: "You do not have permission to open this link.",
            recovery: "Ask your agency for a new secure link if you still need access.",
        }
        : outcome === "expired_access"
            ? {
                title: "This link has expired",
                reason: "The secure client link is no longer valid.",
                recovery: "Ask your agency for a fresh link to continue.",
            }
            : {
                title: "This link is no longer available",
                reason: "The secure client link was revoked.",
                recovery: "Ask your agency for a new link if you still need access.",
            };
    const agency = baseModel({
        role: "AGENCY",
        journeyStage: "Access",
        condition: "NEEDS_ATTENTION",
        title: outcome === "permission_denied"
            ? "Client access was denied"
            : outcome === "expired_access"
                ? "Client link expired"
                : "Client link revoked",
        reason: outcome === "permission_denied"
            ? "A client access attempt failed a permission or tenant check."
            : outcome === "expired_access"
                ? "The client link is past its expiry gate."
                : "The client link was revoked and cannot be used.",
        primaryAction: null,
        secondaryAction: null,
        waitingOn: null,
        blockers: [copy.title],
        progress: null,
        expectedResult: null,
        recovery: "Issue a new secure client link from the project room when appropriate.",
        help: null,
        relevantRecords: [],
        advancedDetails: null,
    });
    const client = baseModel({
        role: "CLIENT",
        journeyStage: "Access",
        condition: "NEEDS_ATTENTION",
        title: copy.title,
        reason: copy.reason,
        primaryAction: null,
        secondaryAction: null,
        waitingOn: null,
        blockers: [],
        progress: null,
        expectedResult: null,
        recovery: copy.recovery,
        help: null,
        relevantRecords: [],
        advancedDetails: null,
    });
    return { outcome, agency, client };
}
function classifyOutcome(input) {
    if (input.accessDenial === "permission_denied")
        return "permission_denied";
    if (input.accessDenial === "expired_access")
        return "expired_access";
    if (input.accessDenial === "revoked_access")
        return "revoked_access";
    const linkStatus = input.clientLink?.status?.trim() ?? "";
    if (linkStatus === "EXPIRED")
        return "expired_access";
    if (linkStatus === "REVOKED")
        return "revoked_access";
    if (input.readiness?.hasBlockingContradiction === true)
        return "contradiction";
    if (input.openMissingInformationClarification === true)
        return "information_missing";
    if (input.readiness &&
        input.readiness.blockedPackages.length > 0 &&
        input.readiness.availablePackages.length === 0) {
        return "package_blocked";
    }
    const lifecycle = input.lifecycle;
    const intakeSubmitted = linkStatus === "INTAKE_SUBMITTED";
    const flags = lifecycle?.flags;
    const hasCompleteClearFlags = Boolean(flags) &&
        typeof flags.qaFailure === "boolean" &&
        typeof flags.generating === "boolean" &&
        typeof flags.packaging === "boolean" &&
        typeof flags.generationLock === "boolean" &&
        flags.qaFailure === false &&
        flags.generating === false &&
        flags.packaging === false &&
        flags.generationLock === false;
    const agencyReadyGates = lifecycle?.simplePhase === "review_and_build" &&
        lifecycle.primaryActionId === "confirm-build" &&
        hasCompleteClearFlags;
    const readyToBuild = intakeSubmitted &&
        agencyReadyGates &&
        Boolean(input.readiness && input.readiness.availablePackages.length > 0);
    if (readyToBuild)
        return "ready_to_build";
    // Agency READY intake_submitted overlaps ENR-UX-006 — require the same lifecycle gates.
    if (intakeSubmitted && agencyReadyGates)
        return "intake_submitted";
    // Submitted without confirm-build readiness: Client waiting only — no Agency READY Start Here.
    if (intakeSubmitted) {
        return null;
    }
    if (linkStatus === "INTAKE_IN_PROGRESS")
        return "intake_in_progress";
    if (lifecycle?.simplePhase === "waiting_for_client" &&
        lifecycle.primaryActionId === "client-link") {
        return "waiting_for_client";
    }
    if (linkStatus === "INTAKE_OPEN" && input.clientLink?.accessReady === true) {
        return "client_access_ready";
    }
    // Missing ClientLink must not be inferred as INTAKE_OPEN.
    if (input.projectExists === true && !input.clientLink) {
        return "new_project";
    }
    return null;
}
function pairForOutcome(outcome, input) {
    if (outcome === "permission_denied" ||
        outcome === "expired_access" ||
        outcome === "revoked_access") {
        return denialPair(outcome);
    }
    const projectId = input.projectId.trim();
    const displayName = resolveDisplayName(input);
    switch (outcome) {
        case "contradiction":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Readiness",
                    condition: "NEEDS_ATTENTION",
                    title: "Resolve conflicting business information",
                    reason: "Authoritative readiness reported a blocking contradiction.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "REVIEW_READINESS",
                        projectId,
                        label: "Review readiness",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Conflicting business information blocks safe build"],
                    progress: null,
                    expectedResult: "Resolved facts unblock package eligibility.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — contradiction`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Business information",
                    condition: "NEEDS_ATTENTION",
                    title: "Confirm a few business details",
                    reason: "Some information needs clarification before the website can move forward.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_CLARIFICATION",
                        label: "Answer questions",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Some business details need confirmation"],
                    progress: null,
                    expectedResult: "Confirmed details help your agency continue.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Open clarification questions"],
                    advancedDetails: null,
                }),
            };
        case "information_missing":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Clarification",
                    condition: "WAITING",
                    title: "Waiting for missing client information",
                    reason: "An open missing-information clarification is awaiting the client.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "REVIEW_CLARIFICATION",
                        projectId,
                        label: "Open clarification",
                    },
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "Client answers unlock the next readiness check.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — missing information`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Business information",
                    condition: "READY",
                    title: "Share a few missing details",
                    reason: "Your agency needs a little more information to continue.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_CLARIFICATION",
                        label: "Answer questions",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "Your answers help the agency continue your website.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Questions from your agency"],
                    advancedDetails: null,
                }),
            };
        case "package_blocked":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Readiness",
                    condition: "NEEDS_ATTENTION",
                    title: "Package eligibility is blocked",
                    reason: "Authoritative readiness reports blocked packages and no available packages.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "REVIEW_READINESS",
                        projectId,
                        label: "Review readiness",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: input.readiness?.missingRequirements.length
                        ? [...input.readiness.missingRequirements]
                        : ["Package eligibility is blocked"],
                    progress: null,
                    expectedResult: "Resolved requirements restore available packages.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — package blocked`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Business information",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your agency is finishing business-information checks before the next step.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your agency will continue once information is ready.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Business information review"],
                    advancedDetails: null,
                }),
            };
        case "ready_to_build":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Review business information",
                    condition: "READY",
                    title: "Confirm the client's business information",
                    reason: "The client submitted their information. Review it before building the next website preview.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "REVIEW_BUSINESS_INFORMATION",
                        projectId,
                        label: "Review business information",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: { current: 3, total: 7, label: "Stage 3 of 7" },
                    expectedResult: "Confirmed information becomes ready for the website build step.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — ready to build`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Intake",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your information was submitted. Your agency is reviewing it before the next step.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your agency will continue once review is complete.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Submitted business information"],
                    advancedDetails: null,
                }),
            };
        case "intake_submitted":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Review business information",
                    condition: "READY",
                    title: "Confirm the client's business information",
                    reason: "The client submitted their information. Review it before building the next website preview.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "REVIEW_BUSINESS_INFORMATION",
                        projectId,
                        label: "Review business information",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: { current: 3, total: 7, label: "Stage 3 of 7" },
                    expectedResult: "Confirmed information becomes ready for the website build step.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — client intake submitted`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Intake",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your information was submitted. Your agency will continue from here.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your agency reviews the submitted information next.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Submitted business information"],
                    advancedDetails: null,
                }),
            };
        case "intake_in_progress":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Intake",
                    condition: "WAITING",
                    title: "Waiting for the client to finish intake",
                    reason: "The client started intake and has not submitted yet.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "OPEN_CLIENT_LINK",
                        projectId,
                        label: "Open client link",
                    },
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "Client submission unlocks agency review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — intake in progress`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Intake",
                    condition: "IN_PROGRESS",
                    title: "Continue your intake",
                    reason: "You started sharing business information. Finish and submit when ready.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_INTAKE",
                        label: "Continue intake",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "Submitted information goes to your agency for review.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Business intake"],
                    advancedDetails: null,
                }),
            };
        case "waiting_for_client":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Access",
                    condition: "WAITING",
                    title: "Waiting for the client",
                    reason: "Share the secure client link so intake can begin.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "OPEN_CLIENT_LINK",
                        projectId,
                        label: "Copy client link",
                    },
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "Client opens the link and starts intake.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — waiting for client`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Access",
                    condition: "READY",
                    title: "Start your intake",
                    reason: "Your secure link is ready. Share your business information to continue.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_INTAKE",
                        label: "Start intake",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "Submitted information goes to your agency.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Business intake"],
                    advancedDetails: null,
                }),
            };
        case "client_access_ready":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Access",
                    condition: "WAITING",
                    title: "Client access is ready",
                    reason: "A secure client link exists. Waiting for the client to open it and start intake.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "OPEN_CLIENT_LINK",
                        projectId,
                        label: "Copy client link",
                    },
                    secondaryAction: null,
                    waitingOn: "Client",
                    blockers: [],
                    progress: null,
                    expectedResult: "Client opens the link and begins intake.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — client access ready`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Access",
                    condition: "READY",
                    title: "Start your intake",
                    reason: "Your secure link is ready. Share your business information to continue.",
                    primaryAction: {
                        intent: "OPEN_CLIENT_INTAKE",
                        label: "Start intake",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "Submitted information goes to your agency.",
                    recovery: null,
                    help: null,
                    relevantRecords: ["Business intake"],
                    advancedDetails: null,
                }),
            };
        case "new_project":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Access",
                    condition: "READY",
                    title: "Create secure client access",
                    reason: "The project exists. Create a secure client link so intake can begin.",
                    primaryAction: {
                        intent: "OPEN_AGENCY_PROJECT_TASK",
                        task: "OPEN_CLIENT_LINK",
                        projectId,
                        label: "Open client link",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "A secure client link becomes available to share.",
                    recovery: null,
                    help: null,
                    relevantRecords: [`${displayName} — new project`],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Access",
                    condition: "WAITING",
                    title: "Waiting for your agency",
                    reason: "Your agency has not shared a secure access link yet.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your agency will share a secure link when ready.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
    }
}
/**
 * Fail-closed paired Agency/Client Access / Intake / Readiness presentation (ENR-UX-008).
 * Consumes authoritative domain outputs only. Never calculates lifecycle, readiness,
 * eligibility, or access gates.
 */
export function derivePairedAccessIntakeReadiness(input) {
    if (!projectIdOk(input))
        return null;
    if (input.lifecycle && input.lifecycle.projectId !== input.projectId.trim()) {
        return null;
    }
    const outcome = classifyOutcome(input);
    if (!outcome)
        return null;
    return pairForOutcome(outcome, input);
}
/**
 * First matching candidate in input order wins (deterministic Agency Start Here).
 * Prefer agency READY / NEEDS_ATTENTION over WAITING / IN_PROGRESS.
 */
export function selectAgencyAccessIntakeReadinessStartHere(candidates) {
    let waitingFallback = null;
    for (const candidate of candidates) {
        const paired = derivePairedAccessIntakeReadiness(candidate);
        if (!paired)
            continue;
        const agency = paired.agency;
        if (agency.condition === "READY" || agency.condition === "NEEDS_ATTENTION") {
            return agency;
        }
        if (!waitingFallback && (agency.condition === "WAITING" || agency.condition === "IN_PROGRESS")) {
            waitingFallback = agency;
        }
    }
    return waitingFallback;
}
export function canonicalizeAccessIntakeReadiness(model) {
    return stableStringify(model);
}
/** Map known access API error codes to denial reasons (presentation only). */
export function accessDenialFromClientApiError(errorCode) {
    const code = errorCode.trim();
    if (code === "client_link_expired")
        return "expired_access";
    if (code === "client_link_revoked")
        return "revoked_access";
    if (code === "client_link_not_found" ||
        code === "invalid_client_link_url" ||
        code === "client_link_missing_token" ||
        code === "unauthorized" ||
        code === "forbidden") {
        return "permission_denied";
    }
    return null;
}
/** Fail-closed Client terminology scan for presentation copy. */
export function clientAccessIntakeContainsProhibitedTerminology(model) {
    if (model.role !== "CLIENT")
        return false;
    const blob = [
        model.title,
        model.reason,
        model.expectedResult ?? "",
        model.recovery ?? "",
        model.help ?? "",
        model.advancedDetails ?? "",
        ...model.blockers,
        ...model.relevantRecords,
        model.primaryAction?.label ?? "",
    ].join("\n");
    return PROHIBITED_CLIENT_TERMS.some((term) => blob.includes(term));
}
//# sourceMappingURL=derive-access-intake-readiness.js.map